/**
 * One bet, with BOTH faces visible (spec #45 §6).
 *
 * `commitment.locked_at == null` renders "not yet committed" and is drawn
 * as the gate it is (dashed), because a belief that nobody has committed
 * to is a real and common state — hiding it would make the plan look
 * fuller than it is.
 *
 * `how_we_know.latest_reading` is printed verbatim or not at all. There is
 * no fallback number here, and no "0" standing in for "not measured yet".
 */

import { Badge, BlockStack, InlineStack, Text } from '@shopify/polaris';

import type { Bet } from '../../types/plan';
import styles from './workspace.module.css';

export function BetCard({ bet }: { bet: Bet }) {
  const committed = bet.commitment.locked_at !== null;
  const reading = bet.commitment.how_we_know.latest_reading;

  return (
    <div
      className={`${styles.betCard} ${committed ? '' : styles.betUncommitted}`}
      data-testid={`bet-card-${bet.id}`}
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {bet.label}
        </Text>
        <InlineStack gap="200" blockAlign="center">
          {committed ? (
            <Badge tone="success">Committed</Badge>
          ) : (
            <Badge>Not yet committed</Badge>
          )}
          <span className={styles.mono}>
            {bet.belief.confidence_now.toFixed(2)} confidence
          </span>
        </InlineStack>
        <Text as="p" tone="subdued" variant="bodySm">
          {bet.belief.statement}
        </Text>
        {bet.commitment.how_we_know.proxy ? (
          <div className={styles.subtle}>
            How we know:{' '}
            <span className={styles.mono}>
              {bet.commitment.how_we_know.proxy}
            </span>
            {bet.commitment.how_we_know.target !== null &&
            bet.commitment.how_we_know.target !== undefined ? (
              <>
                {' · target '}
                <span className={styles.mono}>
                  {String(bet.commitment.how_we_know.target)}
                </span>
              </>
            ) : null}
            {reading !== null && reading !== undefined ? (
              <>
                {' · latest '}
                <span className={styles.mono}>{String(reading)}</span>
              </>
            ) : (
              ' · not measured yet'
            )}
          </div>
        ) : null}
      </BlockStack>
    </div>
  );
}
